import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { equipmentLabelStyle, chemicalLabelStyle } from './styles';

interface InvertedBuretteProps {
  visible: boolean;
  buretteReading: number;
  isReacting: boolean;
  isPaused: boolean;
}

const InvertedBurette: React.FC<InvertedBuretteProps> = ({
  visible, buretteReading, isReacting, isPaused,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const scaleRef = useRef(0);
  const gasBubbleRef = useRef<THREE.InstancedMesh>(null);
  const dummy = useMemo(() => new THREE.Object3D(), []);

  const gasBubbles = useMemo(
    () =>
      Array.from({ length: 20 }, () => ({
        pos: new THREE.Vector3(
          (Math.random() - 0.5) * 0.06,
          -1.2 + Math.random() * 2.4,
          (Math.random() - 0.5) * 0.06
        ),
        speed: Math.random() * 0.015 + 0.008,
        size: Math.random() * 0.018 + 0.01,
        offset: Math.random() * Math.PI * 2,
      })),
    []
  );

  useFrame((state) => {
    if (!groupRef.current) return;
    scaleRef.current += ((visible ? 1 : 0) - scaleRef.current) * 0.06;
    groupRef.current.scale.setScalar(scaleRef.current);

    const time = state.clock.elapsedTime;
    const wh = (buretteReading / 50) * 2.2;
    const waterTop = -1.2 + wh;

    if (gasBubbleRef.current && isReacting && !isPaused) {
      gasBubbles.forEach((b, i) => {
        b.pos.y += b.speed;
        b.pos.x = Math.sin(time * 2 + b.offset) * 0.02;
        b.pos.z = Math.cos(time * 2 + b.offset) * 0.02;
        if (b.pos.y > 1.15) {
          b.pos.y = waterTop + 0.05;
          b.size = Math.random() * 0.016 + 0.008;
        }
        if (b.pos.y < waterTop) {
          b.pos.y = waterTop + 0.05;
        }
        dummy.position.copy(b.pos);
        dummy.scale.setScalar(b.size);
        dummy.updateMatrix();
        gasBubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      gasBubbleRef.current.instanceMatrix.needsUpdate = true;
    } else if (gasBubbleRef.current) {
      gasBubbles.forEach((_, i) => {
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        gasBubbleRef.current!.setMatrixAt(i, dummy.matrix);
      });
      gasBubbleRef.current.instanceMatrix.needsUpdate = true;
    }
  });

  const waterHeight = (buretteReading / 50) * 2.2;
  const gasHeight = Math.max(0, 2.2 - waterHeight);

  return (
    <group ref={groupRef} position={[0.5, -0.15, 0]} scale={0}>
      <mesh>
        <cylinderGeometry args={[0.085, 0.085, 2.4, 24, 1, true]} />
        <meshPhysicalMaterial color="#d8ecf4" transparent opacity={0.25} roughness={0.05} transmission={0.85} thickness={0.3} ior={1.5} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, 1.2, 0]}>
        <circleGeometry args={[0.085, 24]} />
        <meshPhysicalMaterial color="#d8ecf4" transparent opacity={0.35} roughness={0.05} />
      </mesh>
      {waterHeight > 0.05 && (
        <mesh position={[0, -1.2 + waterHeight / 2, 0]}>
          <cylinderGeometry args={[0.07, 0.07, waterHeight, 24]} />
          <meshStandardMaterial color="#78b8e0" transparent opacity={0.7} roughness={0} />
        </mesh>
      )}
      {gasHeight > 0.05 && (
        <mesh position={[0, 1.2 - gasHeight / 2, 0]} renderOrder={2}>
          <cylinderGeometry args={[0.068, 0.068, gasHeight, 24]} />
          <meshStandardMaterial
            color="#c8e8ff"
            transparent
            opacity={0.6}
            roughness={0}
            emissive="#a0d4ff"
            emissiveIntensity={0.12}
            depthWrite={false}
          />
        </mesh>
      )}

      <instancedMesh ref={gasBubbleRef} args={[undefined, undefined, 20]}>
        <sphereGeometry args={[1, 8, 8]} />
        <meshStandardMaterial color="#d0eaff" transparent opacity={0.7} roughness={0.1} emissive="#80c0ff" emissiveIntensity={0.1} />
      </instancedMesh>

      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[0.09, -1.0 + i * 0.55, 0]}>
          <boxGeometry args={[0.035, 0.008, 0.008]} />
          <meshStandardMaterial color="#3a5878" />
        </mesh>
      ))}
      {visible && (
        <Html position={[0, 0, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={equipmentLabelStyle}>Burette</div>
        </Html>
      )}
      {visible && isReacting && gasHeight > 0.1 && (
        <Html position={[0, 1.2 - gasHeight / 2, 0]} center style={{ pointerEvents: 'none' }}>
          <div style={chemicalLabelStyle}>H₂ gas</div>
        </Html>
      )}
    </group>
  );
};

export default InvertedBurette;